/**
 * Static fallen-brick props. Visual only — no physics. Placement is
 * seeded from the removed brick's ledger seed so a stamp sequence is
 * deterministic. Per-face material memory (clay / mortar / weathered /
 * soot) lives on the prop and is the contract that later transfers to
 * the particle lab's chunkSim instances (do not import from src/particles/).
 */
import { hash01 } from './hash';
import type { DamageLedger } from './ledger';
import { clamp01 } from './palette';
import { BRICK_PROFILE } from './profiles';
import type { ResidueStamp } from './residue';
import type { DamageMaterialProfile } from './types';

export const DEBRIS_FRACTION = 0.55;
export const DEBRIS_CAP = 48;

export interface DebrisProp {
  brickIndex: number;
  seed: number;
  x: number;
  y: number;
  z: number;
  rx: number;
  ry: number;
  rz: number;
  hx: number;
  hy: number;
  hz: number;
  /** Bit 0 = +X clay, 1 = -X clay, 2 = +Z clay, 3 = -Z clay. Beds are always mortar. */
  brokenMask: number;
  soot: number;
  tintR: number;
  tintG: number;
  tintB: number;
}

function overlaps(a: DebrisProp, b: DebrisProp): boolean {
  return (
    Math.abs(a.x - b.x) < (a.hx + b.hx) * 0.82
    && Math.abs(a.y - b.y) < (a.hy + b.hy) * 0.78
    && Math.abs(a.z - b.z) < (a.hz + b.hz) * 0.82
  );
}

function restPose(
  seed: number,
  length: number,
  height: number,
  depth: number,
): { rx: number; ry: number; rz: number; hx: number; hy: number; hz: number } {
  const kind = Math.floor(hash01(seed, 3) * 3);
  const jitter = (hash01(seed, 5) - 0.5) * 0.28;
  const yaw = (hash01(seed, 7) - 0.5) * 0.55;
  if (kind === 0) {
    // Lying on a bed face.
    return {
      rx: jitter * 0.25,
      ry: yaw,
      rz: (hash01(seed, 9) - 0.5) * 0.18,
      hx: length * 0.5,
      hy: height * 0.5,
      hz: depth * 0.5,
    };
  }
  if (kind === 1) {
    // Lying on a stretcher (rolled 90° about X).
    return {
      rx: Math.PI * 0.5 + jitter * 0.2,
      ry: yaw * 0.6,
      rz: (hash01(seed, 9) - 0.5) * 0.22,
      hx: length * 0.5,
      hy: depth * 0.5,
      hz: height * 0.5,
    };
  }
  // Tilted on a long edge.
  return {
    rx: 0.72 + jitter * 0.35,
    ry: yaw,
    rz: (hash01(seed, 9) - 0.5) * 0.4,
    hx: length * 0.5,
    hy: Math.max(height, depth) * 0.42,
    hz: Math.max(height, depth) * 0.42,
  };
}

function brokenMask(seed: number, profile: DamageMaterialProfile): number {
  let mask = 0;
  if (profile.debris.alwaysClayHeads) mask = 1 | 2;
  if (hash01(seed, 13) > 1 - profile.debris.stretcherClayChance) mask |= 4;
  if (hash01(seed, 17) > 1 - profile.debris.backStretcherClayChance) mask |= 8;
  return mask;
}

export function syncDebris(
  ledger: DamageLedger,
  stamps: readonly ResidueStamp[],
  prev: readonly DebrisProp[],
  profile: DamageMaterialProfile = BRICK_PROFILE,
): DebrisProp[] {
  const spec = ledger.grid.spec;
  const removed = new Set<number>();
  for (let i = 0; i < ledger.bricks.length; i += 1) {
    if (ledger.bricks[i].state === 'removed') removed.add(i);
  }
  const kept: DebrisProp[] = [];
  for (let i = 0; i < prev.length; i += 1) {
    if (removed.has(prev[i].brickIndex)) kept.push(prev[i]);
  }
  const have = new Set(kept.map((p) => p.brickIndex));
  const length = spec.brickLength;
  const height = spec.brickHeight;
  const depth = spec.brickDepth;
  const front = depth * 0.5 + 0.05;

  const tryPlace = (prop: DebrisProp): boolean => {
    for (let k = 0; k < kept.length; k += 1) {
      if (overlaps(prop, kept[k])) return false;
    }
    if (prop.z < 0.02) return false;
    if (prop.y < prop.hy - 0.002) return false;
    return true;
  };

  const indices = Array.from(removed).sort((a, b) => a - b);
  for (const idx of indices) {
    if (kept.length >= profile.debris.cap) break;
    if (have.has(idx)) continue;
    const brick = ledger.bricks[idx];
    if (hash01(brick.seed, 41) >= profile.debris.fraction) continue;
    const place = ledger.grid.bricks[idx];
    const pose = restPose(brick.seed, length, height, depth);
    let sooted = 0;
    for (let s = 0; s < stamps.length; s += 1) {
      const st = stamps[s];
      if (st.blastFraction <= 0.5) continue;
      const d = Math.hypot(place.x - st.x, place.y - st.y);
      const rS = 0.55 + Math.max(0.2, st.energy) * 0.5;
      if (d < rS * 1.15) sooted = Math.max(sooted, clamp01(1 - d / (rS * 1.15)));
    }
    const slot = 0.21;
    const origin = place.x + (hash01(brick.seed, 43) - 0.5) * 0.08;
    let placed: DebrisProp | null = null;
    for (let attempt = 0; attempt < 18 && !placed; attempt += 1) {
      const side = attempt % 2 === 0 ? 1 : -1;
      const step = Math.ceil(attempt / 2);
      const layer = attempt >= 12 ? 1 : 0;
      const prop: DebrisProp = {
        brickIndex: idx,
        seed: brick.seed,
        x: origin + side * step * slot,
        y: pose.hy + layer * (height * 0.92 + 0.01),
        z: front + hash01(brick.seed, 47) * 0.05 + layer * 0.07,
        rx: pose.rx,
        ry: pose.ry,
        rz: pose.rz,
        hx: pose.hx,
        hy: pose.hy,
        hz: pose.hz,
        brokenMask: brokenMask(brick.seed, profile),
        soot: sooted,
        tintR: clamp01(0.48 + hash01(brick.seed, 3) * 0.07),
        tintG: clamp01(0.2 + hash01(brick.seed, 8) * 0.05),
        tintB: clamp01(0.13 + hash01(brick.seed, 13) * 0.03),
      };
      if (tryPlace(prop)) placed = prop;
    }
    if (!placed) {
      placed = {
        brickIndex: idx,
        seed: brick.seed,
        x: origin + (kept.length % 9 - 4) * 0.24,
        y: pose.hy,
        z: front + 0.16 + (kept.length % 3) * 0.05,
        rx: pose.rx,
        ry: pose.ry,
        rz: pose.rz,
        hx: pose.hx,
        hy: pose.hy,
        hz: pose.hz,
        brokenMask: brokenMask(brick.seed, profile),
        soot: sooted,
        tintR: clamp01(0.48 + hash01(brick.seed, 3) * 0.07),
        tintG: clamp01(0.2 + hash01(brick.seed, 8) * 0.05),
        tintB: clamp01(0.13 + hash01(brick.seed, 13) * 0.03),
      };
    }
    if (placed) {
      kept.push(placed);
      have.add(idx);
    }
  }
  return kept;
}

export function debrisSnapshot(props: readonly DebrisProp[]): string {
  return props
    .map(
      (p) =>
        `${p.brickIndex}:${p.x.toFixed(4)}:${p.y.toFixed(4)}:${p.z.toFixed(4)}:${p.rx.toFixed(3)}:${p.soot.toFixed(3)}:${p.brokenMask}`,
    )
    .join('|');
}
